import { ResizeNotifier } from '@theme/critical';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * フォントサイズの上限（px）
 */
const MAX_FONT_SIZE = 72;

/**
 * コンテナ幅に収まるようにテキストのサイズを自動調整するカスタム要素
 */
class JumboText extends HTMLElement {
  connectedCallback() {
    // 初回計算
    requestAnimationFrame(this.#handleResize);
    if (this.dataset.textEffect && this.dataset.textEffect !== 'none' && !prefersReducedMotion()) {
      this.#setIntersectionObserver();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    if (this.dataset.textEffect && this.dataset.textEffect !== 'none' && !prefersReducedMotion()) {
      this.intersectionObserver?.disconnect();
    }
  }

  /**
   * テキストがビューポートに入ったときにアニメーション用クラスを付与
   */
  #setIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('jumbo-text-visible');
            if (this.dataset.animationRepeat === 'false') {
              this.intersectionObserver.unobserve(entry.target);
            }
          } else {
            this.classList.remove('jumbo-text-visible');
          }
        });
      },
      { threshold: 0.3 }
    );

    this.intersectionObserver.observe(this);
  }

  /**
   * コンテナ幅に収まる最適なフォントサイズを計算（上限72px）
   */
  #calculateOptimalFontSize = () => {
    // 空文字はスキップ
    if (!this.textContent?.trim()) return;

    // 計算中は非表示
    this.classList.remove('ready');

    if (this.offsetWidth <= 0) return;

    // Resize監視を一時停止
    this.#resizeObserver.disconnect();

    // 探索開始時の最小値
    this.style.fontSize = '1px';

    // 最適サイズを探索
    const fontSize = findOptimalFontSize(this, this.offsetWidth);

    // 適用（最終的にも上限でクリップ）
    this.style.fontSize = `${Math.min(fontSize, MAX_FONT_SIZE)}px`;

    // 監視を再開
    this.#resizeObserver.observe(this);

    // 表示
    this.classList.add('ready');
  };

  #handleResize = () => {
    this.#calculateOptimalFontSize();

    // ページ下端付近では cap-text を使うヒントを付与（余白抑止）
    const rect = this.getBoundingClientRect();
    const bottom = rect.bottom + window.scrollY;
    const distanceFromBottom = document.documentElement.offsetHeight - bottom;
    this.dataset.capText = (distanceFromBottom <= 100).toString();
  };

  #resizeObserver = new ResizeNotifier(this.#handleResize);
}

/**
 * 指定サイズでテキストが横幅をはみ出すか判定
 * @param {HTMLElement} element
 * @param {number} containerWidth
 * @param {number} size
 * @returns {boolean}
 */
function checkTextOverflow(element, containerWidth, size) {
  element.style.fontSize = `${size}px`;
  return element.scrollWidth > containerWidth;
}

/**
 * 二分探索で最適フォントサイズを見つける（上限72px）
 * @param {HTMLElement} element
 * @param {number} containerWidth
 * @returns {number}
 */
function findOptimalFontSize(element, containerWidth) {
  // 探索範囲
  let minSize = 1;
  let maxSize = MAX_FONT_SIZE; // 上限を固定
  const precision = 0.5;

  // 初期推定（テキスト長も考慮）＋ 上限適用
  const textLength = element.textContent?.length || 0;
  let fontSize = Math.min(
    maxSize,
    Math.sqrt(containerWidth) * (15 / Math.sqrt(Math.max(1, textLength)))
  );

  // 初期チェックで境界更新
  if (checkTextOverflow(element, containerWidth, fontSize)) {
    maxSize = fontSize;
  } else {
    minSize = fontSize;
  }

  // 二分探索
  let iterations = 0;
  const MAX_ITERATIONS = 30;
  while (maxSize - minSize > precision && iterations < MAX_ITERATIONS) {
    fontSize = (minSize + maxSize) / 2;

    if (checkTextOverflow(element, containerWidth, fontSize)) {
      maxSize = fontSize;
    } else {
      minSize = fontSize;
    }

    iterations++;
  }

  // 安全マージン & 上限クリップ
  return Math.min(minSize * 0.99, MAX_FONT_SIZE);
}

// 二重定義を回避して登録
if (!customElements.get('jumbo-text')) {
  customElements.define('jumbo-text', JumboText);
}